import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import StandardScreen, { ScreenContent } from "../components/layout/StandardScreen";
import Stack from "../components/layout/Stack";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import FormField from "../components/forms/FormField";
import TextInput from "../components/forms/TextInput";
import StatusMessage from "../components/ui/StatusMessage";

const shellStyle = {
maxWidth: "520px",
margin: "0 auto",
paddingTop: "2.5rem",
};

const heroStyle = {
textAlign: "center",
marginBottom: "1.5rem",
};

const brandTitleStyle = {
margin: 0,
fontSize: "2.6rem",
fontWeight: 800,
lineHeight: 1.05,
letterSpacing: "0.3px",
};

const subtitleStyle = {
margin: "0.75rem 0 0",
fontSize: "1.05rem",
fontWeight: 600,
};

const supportingTextStyle = {
margin: "0.4rem 0 0",
fontSize: "0.95rem",
};

const languageSwitcherStyle = {
display: "flex",
justifyContent: "center",
gap: "8px",
marginTop: "12px",
};

const getLangButtonStyle = (isActive) => ({
border: "1px solid var(--color-border)",
background: isActive ? "var(--color-accent)" : "var(--color-surface, white)",
color: isActive ? "white" : "var(--color-text)",
borderRadius: "8px",
padding: "6px 12px",
cursor: "pointer",
fontWeight: 700,
fontSize: "0.9rem",
minWidth: "52px",
transition: "all 0.2s ease",
});

export default function Register({ inAdminMode = false, onClose }) {
const { t, i18n } = useTranslation();
const navigate = useNavigate();

const [form, setForm] = useState({
fullname: "",
userno: "",
password: "",
confirmPassword: "",
role: "",
});

const [message, setMessage] = useState("");
const [verdict, setVerdict] = useState("");
const [passwordValid, setPasswordValid] = useState(false);
const [passwordsMatch, setPasswordsMatch] = useState(false);
const [isLoading, setIsLoading] = useState(false);

useEffect(() => {
const isFourDigit = /^\d{4}$/.test(form.password);
const match = form.password === form.confirmPassword;

setPasswordValid(isFourDigit);
setPasswordsMatch(match);

}, [form.password, form.confirmPassword]);

const changeLanguage = (lng) => {
i18n.changeLanguage(lng);
localStorage.setItem("lang", lng);
};

const handleChange = (e) => {
const { name, value } = e.target;
const numericFields = ["userno", "password", "confirmPassword"];
const nextValue = numericFields.includes(name) ? value.replace(/\D/g, "") : value;
setForm({ ...form, [name]: nextValue });
setMessage("");
setVerdict("");
};

const handleRegister = async (e) => {
e.preventDefault();

if (!passwordValid) {
  setMessage(t("register.validation.invalid_password"));
  setVerdict("cancelled");
  return;
}

if (!passwordsMatch) {
  setMessage(t("register.validation.password_mismatch"));
  setVerdict("cancelled");
  return;
}

try {
  setIsLoading(true);
  setMessage("");
  setVerdict("");

  const { confirmPassword, ...rawPayload } = form;

  const payload = {
    ...rawPayload,
    role: inAdminMode ? rawPayload.role : "payer",
  };

  await axios.post(`${process.env.REACT_APP_API_URL}/auth/register`, payload);

  if (inAdminMode) {
    setMessage(t("register.messages.success_admin"));
    setVerdict("approved");
    setForm({
      fullname: "",
      userno: "",
      password: "",
      confirmPassword: "",
      role: "",
    });
  } else {
    setMessage(t("register.messages.success_user"));
    setVerdict("approved");
    setTimeout(() => navigate("/login"), 1200);
  }
} catch (err) {
  setMessage(
    `${t("register.messages.failure")} ${err.response?.data?.detail || ""}`.trim()
  );
  setVerdict("cancelled");
} finally {
  setIsLoading(false);
}

};

const messageVariant =
verdict === "approved"
? "success"
: message
? "error"
: "info";

return (

<StandardScreen>
  <ScreenContent>
    <div style={shellStyle}>

      {!inAdminMode && (
        <div style={heroStyle}>
          <h1 style={brandTitleStyle}>
            <span style={{ color: "var(--color-text)" }}>bas</span>
            <span style={{ color: "var(--color-accent)" }}>-bin</span>
          </h1>

          <p style={subtitleStyle}>{t("register.punchy_title")}</p>
          <p style={supportingTextStyle}>{t("register.punchy_subtitle")}</p>

          <div style={languageSwitcherStyle}>
            <button
              type="button"
              onClick={() => changeLanguage("en")}
              style={getLangButtonStyle(i18n.language?.toLowerCase().startsWith("en"))}
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => changeLanguage("tr")}
              style={getLangButtonStyle(i18n.language?.toLowerCase().startsWith("tr"))}
            >
              TR
            </button>
          </div>
        </div>
      )}

      <Card title={inAdminMode ? t("register.admin.title") : t("register.title")}>
        <form onSubmit={handleRegister}>
          <Stack gap="4">
            <FormField label={t("register.fields.fullname.label")}>
              <TextInput
                name="fullname"
                value={form.fullname}
                onChange={handleChange}
                placeholder={t("register.fields.fullname.placeholder")}
                disabled={isLoading}
              />
            </FormField>

            <FormField label={t("register.fields.userno.label")}>
              <TextInput
                name="userno"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={form.userno}
                onChange={handleChange}
                placeholder={t("register.fields.userno.placeholder")}
                disabled={isLoading}
              />
            </FormField>

            <FormField label={t("register.fields.password.label")}>
              <TextInput
                name="password"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                value={form.password}
                onChange={handleChange}
                placeholder={t("register.fields.password.placeholder")}
                disabled={isLoading}
              />
            </FormField>

            <FormField label={t("register.fields.confirm_password.label")}>
              <TextInput
                name="confirmPassword"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                value={form.confirmPassword}
                onChange={handleChange}
                placeholder={t("register.fields.confirm_password.placeholder")}
                disabled={isLoading}
              />
            </FormField>

            {inAdminMode && (
              <FormField label={t("register.fields.role.label")}>
                <select
                  name="role"
                  value={form.role}
                  onChange={handleChange}
                  className="form-field"
                  required
                >
                  <option value="">
                    {t("register.fields.role.placeholder")}
                  </option>
                  <option value="payer">payer</option>
                  <option value="vendor">vendor</option>
                </select>
              </FormField>
            )}

            <Button
              type="submit"
              fullWidth
              size="lg"
              disabled={
                isLoading ||
                !passwordValid ||
                !passwordsMatch ||
                (inAdminMode && form.role === "")
              }
            >
              {inAdminMode
                ? t("register.actions.create")
                : t("register.actions.submit")}
            </Button>

            <StatusMessage variant={messageVariant} message={message} />
          </Stack>
        </form>
      </Card>

      {!inAdminMode && (
        <div style={{ marginTop: "1rem", textAlign: "center" }}>
          <p className="form-link-text">
            {t("register.links.have_account")}{" "}
            <Link to="/login" className="form-link">
              {t("register.links.login")}
            </Link>
          </p>

          <p className="form-link-text">
            {t("register.links.return_back")}{" "}
            <Link to="/" className="form-link">
              {t("register.links.return_here")}
            </Link>
          </p>
        </div>
      )}

      {inAdminMode && (
        <div style={{ marginTop: "1rem", textAlign: "center" }}>
          <Button variant="ghost" onClick={onClose}>
            ❌ {t("register.links.close")}
          </Button>
        </div>
      )}
    </div>
  </ScreenContent>
</StandardScreen>

);
}
